import React, { useState, useEffect } from 'react';
import { Landmark, CheckCircle, AlertTriangle, Clock, ChevronRight, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../lib/utils';

export default function ReconciliationWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const data = await apiRequest('/bookkeeper/reconciliation');
      setAccounts(data);
    } catch (error) {
      // Use mock data if API not available
      setAccounts([
        { 
          id: 1, 
          name: 'NAB Business Account', 
          entity: 'Punjab Imports',
          lastReconciled: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'current',
          pendingItems: 0
        },
        { 
          id: 2, 
          name: 'Westpac Trading', 
          entity: 'Punjab Fresh',
          lastReconciled: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'due',
          pendingItems: 12
        },
        { 
          id: 3, 
          name: 'CBA Property Account', 
          entity: 'Punjab Properties',
          lastReconciled: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'overdue',
          pendingItems: 34
        },
        { 
          id: 4, 
          name: 'ANZ Operating', 
          entity: 'Punjab Wholesale',
          lastReconciled: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'current',
          pendingItems: 3
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'current':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'due':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'overdue':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'current':
        return 'bg-green-50 border-green-200';
      case 'due':
        return 'bg-yellow-50 border-yellow-200';
      case 'overdue':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  const getDaysSince = (dateStr) => {
    const today = new Date();
    const lastDate = new Date(dateStr);
    const diffTime = today - lastDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const overdueCount = accounts.filter(a => a.status === 'overdue').length;
  const dueCount = accounts.filter(a => a.status === 'due').length;

  return (
    <Card className="h-full" data-testid="reconciliation-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Landmark className="h-5 w-5 text-indigo-600" />
            Bank Reconciliation
          </CardTitle>
          {expandable && expandPath && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(expandPath)}
              className="text-slate-500 hover:text-slate-700"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <div className="text-lg font-bold text-green-600">
              {accounts.filter(a => a.status === 'current').length}
            </div>
            <div className="text-xs text-green-700">Current</div>
          </div>
          <div className="text-center p-2 bg-yellow-50 rounded-lg">
            <div className="text-lg font-bold text-yellow-600">{dueCount}</div>
            <div className="text-xs text-yellow-700">Due</div>
          </div>
          <div className="text-center p-2 bg-red-50 rounded-lg">
            <div className="text-lg font-bold text-red-600">{overdueCount}</div>
            <div className="text-xs text-red-700">Overdue</div>
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-slate-100 rounded-lg"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {accounts.map((account) => (
              <div 
                key={account.id} 
                className={`p-2.5 rounded-lg border ${getStatusBg(account.status)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {getStatusIcon(account.status)}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{account.name}</p>
                      <p className="text-xs text-slate-500">{account.entity}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {account.pendingItems > 0 && (
                      <span className="text-xs font-medium text-slate-600">
                        {account.pendingItems} items
                      </span>
                    )}
                    <p className="text-xs text-slate-400">
                      {getDaysSince(account.lastReconciled)}d ago
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
